import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import CountUp from "react-countup";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { Reveal, SectionTag, KineticLine } from "@/components/Reveal";
import Ticker from "@/components/Ticker";
import { products, chapters, stats, testimonials, CONTACT } from "@/data/site";

const heroLines = ["METAL, REFINED.", "TRADE, REDEFINED.", "DUBAI TO THE WORLD."];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const bandRef = useRef(null);
  const { scrollYProgress: bandProgress } = useScroll({ target: bandRef, offset: ["start end", "end start"] });
  const bandY = useTransform(bandProgress, [0, 1], ["-12%", "12%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink noise" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src="/assets/hero-scrap.jpg" alt="Processed aluminium scrap" className="h-[120%] w-full object-cover opacity-45 grayscale-[35%]" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-navy/40" />

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-40 pb-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="font-tech text-xs uppercase tracking-[0.3em] text-copper mb-8"
            data-testid="hero-kicker"
          >
            Fluton Global Metalcraft FZCO — Dubai Silicon Oasis, UAE
          </motion.p>

          <h1 className="font-display font-black uppercase text-white leading-[1.05] tracking-tight text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-headline">
            {heroLines.map((line, i) => (
              <KineticLine key={line} text={line} delay={0.25 + i * 0.18} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-8 max-w-xl text-gray-300 text-base md:text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            We export and trade premium recyclable aluminium — from 6063 extrusion scrap and UBC
            bales to EC-grade wire and remelt-ready ingots — from Dubai to furnaces and mills
            across the globe. Verified quality, LME-linked pricing, and logistics that never
            miss a vessel.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-cta-products"
              className="group inline-flex items-center gap-3 bg-copper text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-white hover:text-navy transition-colors duration-300"
            >
              Explore Products
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-3 border border-white/30 text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:border-copper hover:text-copper transition-colors duration-300"
            >
              Get a Quote <ArrowUpRight size={15} />
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="relative z-10 border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4" data-testid="hero-stats">
            {stats.map((s, i) => (
              <div key={s.label} className={`py-8 ${i > 0 ? "border-l border-white/10 pl-6" : ""}`}>
                <p className="font-display font-black text-3xl md:text-4xl text-white">
                  <CountUp end={s.value} enableScrollSpy scrollSpyOnce duration={2} separator="," />
                  <span className="text-copper">{s.suffix}</span>
                </p>
                <p className="font-tech text-[11px] uppercase tracking-[0.2em] text-gray-400 mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <Ticker />

      {/* WHO WE ARE */}
      <section className="py-24 md:py-32" data-testid="home-about-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionTag no="01" label="Who We Are" />
            <Reveal>
              <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02]">
                Exporting trust, <span className="text-copper">one container</span> at a time.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-gray-600 leading-relaxed">
                Fluton Global Metalcraft FZCO is the export and trading arm of the Ollexo Group
                of Companies. While the group sources and imports through India, Fluton moves
                premium recyclable aluminium out of Dubai to buyers across Asia, Europe and Africa.
              </p>
              <p className="mt-4 text-gray-600 leading-relaxed">
                From aluminium 6063 extrusions and UBC bales to EC-grade wire and remelt-ready
                ingots, every lot we trade is inspected, documented and shipped on schedule —
                because in this business, your word is your weight.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="group mt-10 inline-flex items-center gap-3 font-tech text-xs uppercase tracking-[0.2em] text-navy border-b-2 border-copper pb-2 hover:text-copper transition-colors duration-300"
              >
                Our Story
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-copper" />
            <img
              src="/assets/crane-scrap.jpg"
              alt="Crane handling scrap metal"
              className="relative w-full h-[420px] md:h-[520px] object-cover"
              data-testid="home-about-image"
            />
          </Reveal>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="py-24 md:py-32 bg-white border-y border-gray-200" data-testid="home-products-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <SectionTag no="02" label="What We Trade" />
              <Reveal>
                <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02]">
                  The export line.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link
                to="/products"
                data-testid="home-products-viewall"
                className="group inline-flex items-center gap-2 font-tech text-xs uppercase tracking-[0.2em] text-navy hover:text-copper transition-colors duration-300"
              >
                View All Products
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-products-grid">
            {products.slice(0, 5).map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
                <Link
                  to="/products"
                  data-testid={`home-product-card-${i}`}
                  className="group relative block h-full min-h-[260px] overflow-hidden bg-ink"
                >
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-85 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-6">
                    <p className="font-tech text-[11px] uppercase tracking-[0.2em] text-copper">{p.spec}</p>
                    <h3 className="font-display font-bold text-white text-xl md:text-2xl mt-2 tracking-tight">{p.name}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DUBAI HUB — DARK BAND */}
      <section ref={bandRef} className="relative py-28 md:py-40 bg-ink noise overflow-hidden" data-testid="home-dubai-section">
        <motion.div style={{ y: bandY }} className="absolute inset-0 -top-24 -bottom-24">
          <img src="/assets/dubai-cargo.jpg" alt="Cargo vessel at a Dubai port" className="w-full h-full object-cover opacity-30 grayscale-[40%]" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <SectionTag no="03" label="The Dubai Advantage" light />
          <Reveal>
            <h2 className="font-display font-black text-white tracking-tighter text-4xl md:text-6xl leading-[1.0] max-w-3xl">
              Positioned at the <span className="text-copper">crossroads</span> of world trade.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-gray-300 leading-relaxed">
              From Dubai Silicon Oasis, with Jebel Ali port at our doorstep, we connect scrap
              yards in the West to furnaces in the East. Shorter transit, flexible Incoterms,
              and a free-zone structure that keeps your costs lean.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/industries"
              data-testid="home-dubai-link"
              className="group mt-10 inline-flex items-center gap-3 border border-white/30 text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:border-copper hover:text-copper transition-colors duration-300"
            >
              Who We Supply <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="py-24 md:py-32" data-testid="home-why-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="04" label="Why Fluton" />
          <Reveal>
            <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02] mb-16 max-w-2xl">
              Four reasons the trade trusts us.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200" data-testid="home-chapters-grid">
            {chapters.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08}>
                <div
                  className="group h-full border-b border-r border-gray-200 p-10 hover:bg-navy transition-colors duration-500"
                  data-testid={`home-chapter-${c.no}`}
                >
                  <p className="font-tech text-xs tracking-[0.25em] text-copper">[ {c.no} ]</p>
                  <h3 className="font-display font-bold text-2xl text-navy tracking-tight mt-4 group-hover:text-white transition-colors duration-500">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-gray-600 leading-relaxed text-sm group-hover:text-gray-300 transition-colors duration-500">
                    {c.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-white border-y border-gray-200" data-testid="home-testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="05" label="Counterparties" />
          <Reveal>
            <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02] mb-16">
              Word travels. So does metal.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.client} delay={i * 0.1}>
                <figure
                  className="h-full bg-paper border border-gray-200 p-8 flex flex-col hover:-translate-y-1.5 transition-transform duration-300"
                  data-testid={`home-testimonial-${i}`}
                >
                  <Quote size={28} className="text-copper" fill="currentColor" />
                  <blockquote className="mt-6 text-gray-600 text-sm leading-relaxed flex-1">“{t.quote}”</blockquote>
                  <figcaption className="mt-8 pt-6 border-t border-gray-200">
                    <p className="font-display font-bold text-navy">{t.client}</p>
                    <p className="font-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-1">{t.tag}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy noise" data-testid="home-cta-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
          <Reveal>
            <p className="font-tech text-xs uppercase tracking-[0.3em] text-copper mb-6">Ready when you are</p>
            <h2 className="font-display font-black text-white tracking-tighter text-4xl md:text-6xl leading-[1.0] max-w-3xl">
              Ready to move metal?
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="group inline-flex items-center gap-3 bg-copper text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-white hover:text-navy transition-colors duration-300"
              >
                Send an Enquiry
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={CONTACT.phoneHref}
                data-testid="home-cta-call"
                className="inline-flex items-center gap-3 border border-white/30 text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:border-copper hover:text-copper transition-colors duration-300"
              >
                {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
